import React, { useEffect, useState } from 'react';
import { ArrowRight, LogIn, Menu, Plus, Search, User, UserPlus, X } from 'lucide-react';
import { platformRoutes } from './platformRoutes';

// openPalette opens the global command palette from a click by synthesising the
// Cmd/Ctrl+K hotkey the palette already listens for.
function openPalette() {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
}

interface MeResponse {
  authenticated: boolean;
  user?: { id: string; email: string };
}

interface BalanceResponse {
  success: boolean;
  balance?: { total: number };
}

const formatCredits = (credits: number) => credits.toLocaleString();

export const SiteNav: React.FC = () => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadAccount() {
      try {
        const meResponse = await fetch('/api/me').then((response) => response.json());
        if (cancelled) return;
        setMe(meResponse);

        if (meResponse?.authenticated) {
          const balanceResponse: BalanceResponse = await fetch('/api/credits/balance').then((response) => response.json());
          if (!cancelled && balanceResponse?.success) {
            setCredits(balanceResponse.balance?.total ?? 0);
          }
        }
      } catch {
        if (!cancelled) setMe({ authenticated: false });
      }
    }

    loadAccount();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the mobile menu on Escape, and lock body scroll while it is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const isAuthed = Boolean(me?.authenticated);
  const navItems = [
    { label: 'Apps', path: '/apps' },
    { label: 'Projects', path: '/projects' },
    { label: 'Repos', path: '/repos' },
    { label: 'Pulls', path: '/pulls' },
    { label: 'Agent', path: '/agent' },
    { label: 'Chat', path: '/chat' },
    { label: 'Integrations', path: '/integrations' },
    { label: 'Gateway', path: '/gateway' },
    { label: 'Models', path: '/models' },
    { label: 'Evals', path: '/evals' },
    { label: 'Tools', path: '/tools' },
    { label: 'Datasets', path: '/datasets' },
    { label: 'MCP', path: '/mcp-servers' },
    { label: 'Deploys', path: '/deploys' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'Blog', path: '/blog' },
    { label: 'Docs', path: '/docs' },
    { label: 'Assistants', path: '/assistants' },
    { label: 'Characters', path: '/characters' },
    { label: 'Art', path: '/art' },
    { label: 'Music', path: '/music' },
  ].filter(
    (item) =>
      ['/apps', '/repos', '/pulls', '/tools', '/datasets', '/integrations', '/mcp-servers', '/blog', '/assistants', '/characters', '/art', '/music', '/models', '/evals'].includes(item.path) ||
      platformRoutes.some((route) => route.path === item.path),
  );

  return (
    <nav className="fixed top-0 z-50 w-full pointer-events-none">
      <div className="flex h-16 w-full items-center justify-between gap-3 border-b border-white/10 bg-black/86 px-4 shadow-2xl shadow-black/30 backdrop-blur-md sm:px-6 lg:px-8 2xl:px-10">
        <a href="/" className="pointer-events-auto inline-flex min-w-0 items-center gap-2 font-display text-xl font-bold tracking-tight text-white">
          <img
            src="/assets/app-logo-64.webp"
            alt="app.nz"
            className="h-8 w-8 object-contain"
            width="64"
            height="64"
            decoding="async"
          />
          <span className="truncate">app</span>
        </a>

        <button
          type="button"
          onClick={openPalette}
          className="pointer-events-auto hidden h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.05] px-3 text-sm text-white/55 transition-colors hover:bg-white/10 hover:text-white lg:inline-flex"
          aria-label="Search (press / or Cmd+K)"
        >
          <Search size={15} />
          <span>Search</span>
          <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-white/40">/</kbd>
        </button>

        <div className="pointer-events-auto hidden min-w-0 items-center gap-4 overflow-x-auto whitespace-nowrap text-sm font-medium text-white/58 lg:flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {navItems.map((item) => (
            <a key={item.path} href={item.path} className="shrink-0 transition-colors hover:text-white">
              {item.label}
            </a>
          ))}
        </div>

        <div className="pointer-events-auto flex shrink-0 items-center gap-2">
          {isAuthed ? (
            <>
              <a
                href="/account#topup"
                className="hidden h-9 items-center gap-2 rounded-lg border border-white/10 bg-white/[0.06] px-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black sm:inline-flex sm:px-4"
                aria-label="Top up account credits"
              >
                <span>{credits === null ? 'Credits' : `${formatCredits(credits)} credits`}</span>
                <Plus size={16} />
              </a>
              <a
                href="/account"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-bold uppercase text-black ring-1 ring-white/20 transition-transform hover:scale-105"
                aria-label="Account"
                title={me?.user?.email || 'Account'}
              >
                {me?.user?.email ? me.user.email[0].toUpperCase() : <User size={16} />}
              </a>
            </>
          ) : (
            <>
              <a
                href="/login"
                aria-label="Sign in"
                className="inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-lg border border-white/10 bg-white/[0.06] px-3 text-sm font-semibold text-white transition-colors hover:bg-white hover:text-black sm:px-4"
              >
                <LogIn size={16} />
                <span className="hidden sm:inline">Sign in</span>
              </a>
              <a
                href="/signup"
                className="hidden h-9 items-center gap-2 whitespace-nowrap rounded-lg bg-sky-400 px-3 text-sm font-bold text-black transition-colors hover:bg-sky-300 sm:inline-flex sm:px-4"
              >
                <UserPlus size={16} className="sm:hidden" />
                <span>Start building</span>
                <ArrowRight size={15} />
              </a>
            </>
          )}

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-white transition-colors hover:bg-white hover:text-black lg:hidden"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="site-nav-mobile"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <>
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
            className="pointer-events-auto fixed inset-0 z-40 cursor-default bg-black/40 backdrop-blur-sm lg:hidden"
          />
          <div
            id="site-nav-mobile"
            className="pointer-events-auto absolute left-3 right-3 top-16 z-50 max-h-[calc(100vh-5rem)] overflow-y-auto rounded-xl border border-white/10 bg-black/92 p-2 shadow-2xl shadow-black/50 backdrop-blur-md sm:left-5 sm:right-5 lg:hidden"
          >
            <div className="grid grid-cols-2 gap-1">
              {navItems.map((item) => (
                <a
                  key={item.path}
                  href={item.path}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm font-medium text-white/72 transition-colors hover:bg-white/[0.08] hover:text-white"
                >
                  {item.label}
                </a>
              ))}
            </div>

            <div className="mt-2 border-t border-white/10 pt-2">
              {isAuthed ? (
                <div className="grid gap-1">
                  <a
                    href="/account#topup"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-white/72 transition-colors hover:bg-white/[0.08] hover:text-white"
                  >
                    <span>{credits === null ? 'Top up credits' : `${formatCredits(credits)} credits`}</span>
                    <Plus size={16} />
                  </a>
                  <a
                    href="/account"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-sm font-bold text-black transition-colors hover:bg-white/86"
                  >
                    <User size={16} />
                    <span>Account</span>
                  </a>
                </div>
              ) : (
                <div className="grid gap-1">
                  <a
                    href="/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-white/72 transition-colors hover:bg-white/[0.08] hover:text-white"
                  >
                    <LogIn size={16} />
                    <span>Sign in</span>
                  </a>
                  <a
                    href="/signup"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-center gap-2 rounded-lg bg-sky-400 px-3 py-2.5 text-sm font-bold text-black transition-colors hover:bg-sky-300"
                  >
                    <span>Start building</span>
                    <ArrowRight size={15} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </nav>
  );
};
